/* ══════════════════════════════════════════════════════════════
   Feature Flags — Canonical toggles for phased rollout
   Phase 0 baseline requires these flags to exist in config.
   ══════════════════════════════════════════════════════════════ */

export const FEATURE_FLAG_KEYS = [
    'terminal_ui_v2',
    'charts_v2_engine',
    'chat_ui_v1',
    'marketing_site_v1',
    'strict_live_data_only',
] as const;

export type FeatureFlagKey = typeof FEATURE_FLAG_KEYS[number];

export type FeatureFlags = Record<FeatureFlagKey, boolean>;

function parseEnvFlag(value: unknown, fallback: boolean): boolean {
    if (typeof value !== 'string') return fallback;
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') return false;
    return fallback;
}

function getFlagFromEnv(flag: FeatureFlagKey): boolean | null {
    const envKey = `VITE_FEATURE_${flag.toUpperCase()}`;
    const value = (import.meta.env as Record<string, unknown>)[envKey];
    if (typeof value !== 'string') return null;
    return parseEnvFlag(value, false);
}

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
    terminal_ui_v2: false,
    charts_v2_engine: false,
    chat_ui_v1: false,
    marketing_site_v1: false,
    strict_live_data_only: true,
};

export function getFeatureFlags(): FeatureFlags {
    const flags: FeatureFlags = { ...DEFAULT_FEATURE_FLAGS };

    for (const key of FEATURE_FLAG_KEYS) {
        const envValue = getFlagFromEnv(key);
        if (envValue !== null) {
            flags[key] = envValue;
        }
    }

    return flags;
}

export function getFeatureFlag(flag: FeatureFlagKey): boolean {
    return getFeatureFlags()[flag];
}

export function isStrictLiveDataOnly(): boolean {
    return getFeatureFlag('strict_live_data_only');
}
